import { z } from 'zod';
import { fieldsForType, MetaError } from './meta.js';
/** Пустые значения формы ('' / null / [] / NaN) считаются «не заполнено». */
function isEmpty(value) {
    return (value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'number' && Number.isNaN(value)));
}
const toNumber = (value) => typeof value === 'string' && value.trim() !== '' ? Number(value.replace(',', '.')) : value;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const PARTIAL_DATE = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;
const MONEY_AMOUNT = /^\d{1,12}(\.\d{1,4})?$/;
function validCalendarDate(y, m, d) {
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
function enumCodes(meta, def) {
    const options = def.enumSet ? meta.enums[def.enumSet] : undefined;
    if (!options?.length)
        throw new MetaError(`Поле ${def.code}: нет значений для набора ${def.enumSet}`);
    return options.map((o) => o.code);
}
function listItemSchema(itemType) {
    switch (itemType) {
        case 'text':
            return z.string().trim().min(1).max(500);
        case 'url':
            return z.url({ protocol: /^https?$/ });
        case 'catalog_number':
            return z.object({
                system: z.string().min(1).max(32),
                number: z.string().trim().min(1).max(64),
            });
        default: {
            const exhaustive = itemType;
            throw new MetaError(`Неизвестный тип элементов списка: ${String(exhaustive)}`);
        }
    }
}
/**
 * Схема значения одного поля. Исчерпывающий switch: добавить DataType без ветки валидации нельзя —
 * TypeScript не соберёт проект (docs/PLAN.md, раздел 6.2).
 */
export function fieldValueSchema(meta, def) {
    const v = def.validation;
    const dataType = def.dataType;
    switch (dataType) {
        case 'text': {
            let s = z
                .string()
                .trim()
                .max(v.maxLength ?? 500);
            if (v.pattern)
                s = s.regex(new RegExp(v.pattern), 'pattern');
            return s;
        }
        case 'longtext':
            return z
                .string()
                .trim()
                .max(v.maxLength ?? 20_000);
        case 'integer': {
            let n = z.number().int();
            if (v.min !== undefined)
                n = n.min(v.min);
            if (v.max !== undefined)
                n = n.max(v.max);
            return z.preprocess(toNumber, n);
        }
        case 'decimal': {
            let n = z.number().finite();
            if (v.min !== undefined)
                n = n.min(v.min);
            if (v.max !== undefined)
                n = n.max(v.max);
            return z.preprocess(toNumber, n);
        }
        case 'boolean':
            return z.boolean();
        case 'date':
            return z.string().refine((s) => {
                const m = ISO_DATE.exec(s);
                return Boolean(m && validCalendarDate(Number(m[1]), Number(m[2]), Number(m[3])));
            }, 'date');
        case 'partial_date':
            return z.string().refine((s) => {
                const m = PARTIAL_DATE.exec(s);
                if (!m)
                    return false;
                const month = m[2] ? Number(m[2]) : 1;
                const day = m[3] ? Number(m[3]) : 1;
                return month >= 1 && month <= 12 && validCalendarDate(Number(m[1]), month, day);
            }, 'partial_date');
        case 'year_range': {
            const year = z.preprocess(toNumber, z
                .number()
                .int()
                .min(v.min ?? -3000)
                .max(v.max ?? 2200));
            return z
                .object({ from: year, to: z.preprocess((x) => (isEmpty(x) ? null : x), year.nullable()) })
                .refine((r) => r.to === null || r.to >= r.from, {
                message: 'year_range_order',
                path: ['to'],
            });
        }
        case 'enum':
            return z.enum(enumCodes(meta, def));
        case 'multi_enum':
            return z
                .array(z.enum(enumCodes(meta, def)))
                .max(v.maxItems ?? 50)
                .refine((a) => new Set(a).size === a.length, 'duplicates');
        case 'ref':
            return z.uuid();
        case 'money': {
            const currencies = meta.currencies.map((c) => c.code);
            if (!currencies.length)
                throw new MetaError('Нет валют в метаданных');
            return z.object({
                amount: z.preprocess((x) => typeof x === 'number'
                    ? String(x)
                    : typeof x === 'string'
                        ? x.trim().replace(',', '.')
                        : x, z.string().regex(MONEY_AMOUNT, 'money_amount')),
                currency: z.enum(currencies),
            });
        }
        case 'grade':
            return z.object({ scale: z.string(), value: z.string() }).superRefine((g, ctx) => {
                const scale = meta.gradeScales.find((s) => s.code === g.scale);
                if (!scale)
                    ctx.addIssue({ code: 'custom', message: 'grade_scale', path: ['scale'] });
                else if (!scale.values.some((x) => x.code === g.value))
                    ctx.addIssue({ code: 'custom', message: 'grade_value', path: ['value'] });
            });
        case 'list':
            return z.array(listItemSchema(def.listOf ?? 'text')).max(v.maxItems ?? 50);
        case 'url':
            return z.url({ protocol: /^https?$/ });
        default: {
            const exhaustive = dataType;
            throw new MetaError(`Неизвестный тип данных: ${String(exhaustive)}`);
        }
    }
}
function withPresence(schema, required) {
    if (!required)
        return z.preprocess((value) => (isEmpty(value) ? undefined : value), schema.optional());
    // Не .optional().refine(): в Zod 4 отсутствующий ключ с опциональной схемой пропускается целиком,
    // и обязательное поле, не присланное в JSON, проходило бы проверку.
    return z
        .unknown()
        .transform((value, ctx) => {
        if (isEmpty(value)) {
            ctx.addIssue({ code: 'custom', message: 'required' });
            return z.NEVER;
        }
        return value;
    })
        .pipe(schema);
}
/**
 * Динамическая схема полей типа на уровне заполнения — одна для API, форм и desktop
 * (docs/PLAN.md, раздел 6.1). Неизвестные ключи отбрасываются.
 */
export function buildAttributesSchema(meta, typeCode, level, { storage = 'attributes' } = {}) {
    const shape = {};
    for (const field of fieldsForType(meta, typeCode, level)) {
        if (storage === 'attributes' && field.def.storage.kind !== 'attributes')
            continue;
        shape[field.def.code] = withPresence(fieldValueSchema(meta, field.def), field.required);
    }
    return z
        .object(shape)
        .transform((values) => Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined)));
}
//# sourceMappingURL=attributes.js.map